import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { useForm } from "react-hook-form";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Textarea } from "@/components/ui/textarea";
import { CirclePlus, Loader } from "lucide-react";
import { useEffect, useState } from "react";
import { cn } from "@/lib/utils";
import { colors } from "@/utils/colors";
import { toast } from "sonner";
import { createLeftoverProductProcess } from "@/Api/leftoverProducts";
import { getContestProductsUnityBalances } from "@/Api/leftovers";
import { getSelectContests } from "@/Api/contests";
import { getSelectUnities } from "@/Api/unities";
import { getSelectProductGroups } from "@/Api/productGroups";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { ContestProductBalance } from "@/types/leftovers";
import { Mask } from "@/utils/mask";
import { useAuth } from "@/hooks/auth";

interface ConfirmationData {
  unity: any;
  contest: any;
  description: string;
  files: File[];
  products: Array<{
    id: number;
    quota_quantity: number;
    product: ContestProductBalance;
  }>;
  payload: {
    unity_id: number;
    contest_id: number;
    description?: string;
    files: File[];
    contest_product_classifications: Array<{
      id: number;
      quota_quantity: number;
    }>;
  };
}

export const AddBtn = ({ updateFront }: { updateFront: (data: any) => void }) => {
  const form = useForm();

  const [isLoad, setIsLoad] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);
  const [lotteries, setLotteries] = useState<
    Array<{ id: number; name: string }>
  >([]);
  const [contests, setContests] = useState<any[]>([]);
  const [unities, setUnities] = useState<any[]>([]);
  const [contestProducts, setContestProducts] = useState<
    ContestProductBalance[]
  >([]);
  const [selectedLottery, setSelectedLottery] = useState("");
  const [selectedContest, setSelectedContest] = useState("");
  const [selectedUnity, setSelectedUnity] = useState("");
  const [selectedProducts, setSelectedProducts] = useState<{
    [key: number]: { selected: boolean; quantity: number };
  }>({});
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [confirmationData, setConfirmationData] =
    useState<ConfirmationData | null>(null);
  const user = useAuth();
  const userUnityId = user?.user?.unity?.[0]?.id || "";

  useEffect(() => {
    if (isOpen) {
      fetchInitialData();
    }
  }, [isOpen]);

  useEffect(() => {
    if (selectedLottery) {
      fetchContests();
    }
  }, [selectedLottery]);

  useEffect(() => {
    if (selectedUnity && selectedContest) {
      fetchContestProducts();
    }
  }, [selectedUnity, selectedContest]);

  const fetchInitialData = async () => {
    try {
      const [lotteriesResponse, unitiesResponse] = await Promise.all([
        getSelectProductGroups(),
        getSelectUnities(),
      ]);

      setLotteries(lotteriesResponse.data?.response || []);
      if (userUnityId) {
        setUnities(
          unitiesResponse.data?.response.filter(
            (unity) => unity.id == userUnityId
          )
        );
      } else {
        setUnities(unitiesResponse.data?.response);
      }
    } catch (error) {
      console.error("Erro ao buscar dados iniciais:", error);
      toast.error("Erro ao carregar dados");
    }
  };

  const fetchContests = async () => {
    try {
      const contestsResponse = await getSelectContests(
        parseInt(selectedLottery)
      );
      setContests(contestsResponse.data?.response || []);
      setSelectedContest(""); // Reset contest when lottery changes
      setContestProducts([]); // Reset products
    } catch (error) {
      console.error("Erro ao buscar concursos:", error);
      toast.error("Erro ao carregar concursos");
    }
  };

  const fetchContestProducts = async () => {
    try {
      const { data } = await getContestProductsUnityBalances(
        selectedUnity,
        selectedContest
      );
      const productsWithBalance = (data?.response || []).filter(
        (product: ContestProductBalance) => product.unity_balance > 0
      );
      setContestProducts(productsWithBalance);

      const initialSelection: {
        [key: number]: { selected: boolean; quantity: number };
      } = {};
      productsWithBalance.forEach((product: ContestProductBalance) => {
        initialSelection[product.cp_classification_id] = {
          selected: true, // Automatically selected
          quantity: product.unity_balance || 0,
        };
      });
      setSelectedProducts(initialSelection);
    } catch (error) {
      console.error("Erro ao buscar produtos do concurso:", error);
      toast.error("Erro ao carregar produtos do concurso");
    }
  };

  const formSubmit = async (data: any) => {
    // Validate files
    if (!selectedFiles || selectedFiles.length === 0) {
      toast.error("Pelo menos um arquivo é obrigatório");
      return;
    }

    const selectedProductsList = contestProducts
      .filter(
        (product) => selectedProducts[product.cp_classification_id]?.selected
      )
      .map((product) => ({
        id: product.cp_classification_id,
        quota_quantity:
          selectedProducts[product.cp_classification_id]?.quantity || 0,
        product: product,
      }));

    const selectedUnityData = unities.find(
      (u) => u.id === parseInt(selectedUnity)
    );
    const selectedContestData = contests.find(
      (c) => c.id === parseInt(selectedContest)
    );

    const description = data.description || "";

    setConfirmationData({
      unity: selectedUnityData,
      contest: selectedContestData,
      description,
      files: selectedFiles,
      products: selectedProductsList,
      payload: {
        unity_id: parseInt(selectedUnity),
        contest_id: parseInt(selectedContest),
        description: description || undefined,
        files: selectedFiles,
        contest_product_classifications: selectedProductsList.map((p) => ({
          id: p.id,
          quota_quantity: p.quota_quantity,
        })),
      },
    });

    setIsConfirmOpen(true);
  };

  const handleConfirmCreate = async () => {
    if (!confirmationData) return;

    setIsLoad(true);

    try {
      const formData = new FormData();
      formData.append("contest_id", confirmationData.payload.contest_id.toString());
      formData.append("unity_id", confirmationData.payload.unity_id.toString());
      formData.append("lottery_id", selectedLottery);
      if (confirmationData.payload.description) {
        formData.append("description", confirmationData.payload.description);
      }

      // Append multiple files
      confirmationData.payload.files.forEach((file) => {
        formData.append("files[]", file);
      });

      confirmationData.payload.contest_product_classifications.forEach((item, index) => {
        formData.append(`contest_product_classifications[${index}][id]`, item.id.toString());
        formData.append(`contest_product_classifications[${index}][quota_quantity]`, item.quota_quantity.toString());
      });

      const response = await createLeftoverProductProcess(formData);
      const apiResponse = response.data;

      if (!response?.ok) {
        setIsLoad(false);
        return;
      }

      toast.success(apiResponse?.message || "Sobra criada com sucesso!");
      setIsOpen(false);
      setIsConfirmOpen(false);
      form.reset();

      updateFront({ shouldRefresh: true });
    } catch (error) {
      console.error("Erro ao criar sobra:", error);
      toast.error("Erro ao criar sobra");
    } finally {
      setIsLoad(false);
    }
  };

  useEffect(() => {
    if (!isOpen) {
      form.reset({
        unity_id: "",
        lottery_id: "",
        contest_id: "",
        description: "",
      });
      setSelectedLottery("");
      setSelectedContest("");
      setSelectedUnity("");
      setSelectedFiles([]);
      setContests([]);
      setContestProducts([]);
      setSelectedProducts({});
      setConfirmationData(null);
      setIsConfirmOpen(false);
    }
  }, [isOpen, form]);

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogTrigger>
        <Button
          variant="outline"
          className={cn("py-3 flex gap-1", `bg-primary`)}
        >
          <CirclePlus className="size-5" strokeWidth={1.75} />
          Criar Sobra
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-[600px] max-h-[80vh] overflow-y-auto">
        <DialogHeader className="flex flex-col gap-2">
          <DialogDescription className="text-lg">Criar Sobra</DialogDescription>
          <DialogTitle className="text-2xl">Informações da Sobra</DialogTitle>
        </DialogHeader>
        <Form {...form}>
          <form className="space-y-4" onSubmit={form.handleSubmit(formSubmit)}>
            <div className="grid grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="unity_id"
                rules={{ required: "Campo Obrigatório" }}
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>
                      Unidade:
                      <FormMessage />
                    </FormLabel>
                    <Select
                      onValueChange={(value) => {
                        field.onChange(value);
                        setSelectedUnity(value);
                      }}
                      value={field.value}
                    >
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Selecione a unidade" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {unities.map((unity) => (
                          <SelectItem key={unity.id} value={String(unity.id)}>
                            {unity.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="lottery_id"
                rules={{ required: "Campo Obrigatório" }}
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>
                      Loteria:
                      <FormMessage />
                    </FormLabel>
                    <Select
                      onValueChange={(value) => {
                        field.onChange(value);
                        setSelectedLottery(value);
                      }}
                      value={field.value}
                    >
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Selecione a loteria" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {lotteries.map((lottery) => (
                          <SelectItem
                            key={lottery.id}
                            value={String(lottery.id)}
                          >
                            {lottery.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </FormItem>
                )}
              />
            </div>

            <div className="grid grid-cols-1 gap-4">
              <FormField
                control={form.control}
                name="contest_id"
                rules={{ required: "Campo Obrigatório" }}
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>
                      Concurso:
                      <FormMessage />
                    </FormLabel>
                    <Select
                      onValueChange={(value) => {
                        field.onChange(value);
                        setSelectedContest(value);
                      }}
                      value={field.value}
                      disabled={!selectedLottery}
                    >
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue
                            placeholder={
                              selectedLottery
                                ? "Selecione o concurso"
                                : "Primeiro selecione uma loteria"
                            }
                          />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {contests.map((contest) => (
                          <SelectItem
                            key={contest.id}
                            value={String(contest.id)}
                          >
                            {contest.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </FormItem>
                )}
              />
            </div>

            <div className="grid grid-cols-1 gap-4">
              <FormField
                control={form.control}
                name="description"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Descrição (Opcional):</FormLabel>
                    <FormControl>
                      <Textarea
                        placeholder="Descrição da sobra..."
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <div className="grid grid-cols-1 gap-4">
              <div className="space-y-2">
                <label className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70">
                  Arquivos: *
                </label>
                <input
                  type="file"
                  accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                  multiple
                  onChange={(e) => {
                    const files = e.target.files;
                    if (files) {
                      setSelectedFiles(Array.from(files));
                    }
                  }}
                  className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                />
                {selectedFiles.length > 0 && (
                  <div className="space-y-1">
                    {selectedFiles.map((file, index) => (
                      <p
                        key={index}
                        className="text-sm text-green-600 flex items-center gap-2"
                      >
                        📎 {file.name}
                        <span className="text-xs text-gray-500">
                          ({(file.size / 1024).toFixed(1)} KB)
                        </span>
                        <button
                          type="button"
                          onClick={() => {
                            setSelectedFiles((prev) =>
                              prev.filter((_, i) => i !== index)
                            );
                          }}
                          className="text-red-500 hover:text-red-700 ml-auto"
                        >
                          ✕
                        </button>
                      </p>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {selectedUnity && selectedContest && (
              <div className="space-y-3">
                <h3 className="text-lg font-semibold">
                  Produtos que serão incluídos na sobra
                </h3>
                <p className="text-sm text-gray-600">
                  Todos os produtos com saldo disponível serão automaticamente
                  incluídos na sobra.
                </p>
                {contestProducts.length > 0 ? (
                  <div className="space-y-2 max-h-60 overflow-y-auto">
                    {contestProducts.map((product) => {
                      const maxQuantity = product.unity_balance || 0;

                      return (
                        <div
                          key={product.id}
                          className="flex items-center gap-3 p-3 border rounded-lg bg-green-50 border-green-200"
                        >
                          <div className="w-6 h-6 rounded-full bg-green-500 flex items-center justify-center">
                            <span className="text-white text-sm font-bold">
                              ✓
                            </span>
                          </div>
                          <div className="flex-1">
                            <div className="font-medium">
                              {product.contest_product_classification
                                ?.product_name || "Produto"}
                            </div>
                            <div className="text-sm text-gray-500">
                              {product.contest_product_classification
                                ?.product_classification?.name || ""}
                            </div>
                            <div className="text-sm text-blue-600 font-medium">
                              Saldo que será incluído: {maxQuantity} cotas
                            </div>
                            {maxQuantity > 0 && (
                              <div className="text-xs text-green-600">
                                Valor unitário: R${" "}
                                {(
                                  product.contest_product_classification
                                    ?.product_classification?.sale_price || 0
                                ).toFixed(2)}
                              </div>
                            )}
                          </div>
                          <div className="text-right">
                            <div className="text-lg font-bold text-green-600">
                              {maxQuantity}
                            </div>
                            <div className="text-xs text-gray-500">cotas</div>
                            <div className="text-xs text-green-600 font-medium">
                              Valor: R${" "}
                              {(
                                (product.contest_product_classification
                                  ?.product_classification?.sale_price || 0) *
                                maxQuantity
                              ).toFixed(2)}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div className="text-center p-6 bg-blue-50 border border-blue-200 rounded-lg">
                    <p className="text-blue-800 font-medium">
                      Nenhum produto com saldo disponível para esta unidade e
                      concurso.
                    </p>
                    <p className="text-sm mt-2 text-blue-600">
                      A sobra será criada sem produtos, apenas com descrição e
                      arquivo.
                    </p>
                  </div>
                )}
              </div>
            )}

            <DialogFooter className="mt-6">
              <Button
                type="submit"
                className={cn("flex gap-2 mt-1", colors.bg_primary)}
                disabled={
                  isLoad ||
                  !selectedUnity ||
                  !selectedContest ||
                  selectedFiles.length === 0
                }
              >
                {isLoad ? (
                  <>
                    <Loader className="animate-spin size-4" /> Criando
                  </>
                ) : (
                  <>Criar Sobra</>
                )}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>

      <Dialog open={isConfirmOpen} onOpenChange={setIsConfirmOpen}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>Confirmar Criação de Sobra</DialogTitle>
            <DialogDescription>
              Revise os dados antes de criar a sobra
            </DialogDescription>
          </DialogHeader>

          {confirmationData && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <h4 className="font-medium text-sm text-gray-700">Unidade</h4>
                  <p className="text-sm">{confirmationData.unity?.name}</p>
                </div>
                <div>
                  <h4 className="font-medium text-sm text-gray-700">
                    Concurso
                  </h4>
                  <p className="text-sm">{confirmationData.contest?.name}</p>
                </div>
              </div>

              {confirmationData.description && (
                <div>
                  <h4 className="font-medium text-sm text-gray-700">
                    Descrição
                  </h4>
                  <p className="text-sm bg-gray-50 p-2 rounded">
                    {confirmationData.description}
                  </p>
                </div>
              )}

              <div>
                <h4 className="font-medium text-sm text-gray-700 mb-2">
                  Arquivos ({confirmationData.files?.length})
                </h4>
                <div className="space-y-1">
                  {confirmationData.files?.map((file, index) => (
                    <p
                      key={index}
                      className="text-sm bg-gray-50 p-2 rounded flex items-center gap-2"
                    >
                      📎 {file.name}
                      <span className="text-xs text-gray-500">
                        ({(file.size / 1024).toFixed(1)} KB)
                      </span>
                    </p>
                  ))}
                </div>
              </div>

              {confirmationData.products.length > 0 ? (
                <div>
                  <h4 className="font-medium text-sm text-gray-700 mb-2">
                    Produtos que serão incluídos automaticamente
                  </h4>
                  <div className="space-y-2 max-h-60 overflow-y-auto">
                    {confirmationData.products.map((item) => (
                      <div
                        key={item.id}
                        className="flex justify-between items-center p-2 bg-gray-50 rounded"
                      >
                        <div className="flex-1">
                          <div className="font-medium text-sm">
                            {
                              item.product.contest_product_classification
                                ?.product_name
                            }
                          </div>
                          <div className="text-xs text-gray-500">
                            {
                              item.product.contest_product_classification
                                ?.product_classification?.name
                            }
                          </div>
                        </div>
                        <div className="text-right">
                          <div className="font-bold text-blue-600">
                            {item.quota_quantity} cotas
                          </div>
                          <div className="text-xs text-gray-500">
                            R${" "}
                            {(
                              (item.product.contest_product_classification
                                ?.product_classification?.sale_price || 0) *
                              item.quota_quantity
                            ).toFixed(2)}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>

                  <div className="border-t pt-4 mt-4">
                    <div className="flex justify-between items-center font-medium">
                      <span>Total de cotas:</span>
                      <span className="text-blue-600">
                        {confirmationData.products.reduce(
                          (total, item) => total + item.quota_quantity,
                          0
                        )}{" "}
                        cotas
                      </span>
                    </div>
                    <div className="flex justify-between items-center font-medium">
                      <span>Valor total:</span>
                      <span className="text-green-600">
                        {Mask.formatCurrency(
                          confirmationData.products.reduce((total, item) => {
                            const value =
                              item.product.contest_product_classification
                                ?.product_classification?.sale_price || 0;
                            return total + value * item.quota_quantity;
                          }, 0)
                        )}
                      </span>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                  <h4 className="font-medium text-sm text-blue-800 mb-1">
                    Sobra sem produtos
                  </h4>
                  <p className="text-xs text-blue-600">
                    Esta sobra será criada apenas com a descrição e arquivo, sem
                    produtos associados.
                  </p>
                </div>
              )}
            </div>
          )}

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setIsConfirmOpen(false)}
              disabled={isLoad}
            >
              Cancelar
            </Button>
            <Button
              onClick={handleConfirmCreate}
              className={cn("flex gap-2", colors.bg_primary)}
              disabled={isLoad}
            >
              {isLoad ? (
                <>
                  <Loader className="animate-spin size-4" /> Criando
                </>
              ) : (
                <>Confirmar e Criar</>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Dialog>
  );
};
